import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, RefreshCw, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { classifyErr } from "@/lib/admin";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";

export default function Lookups() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("lookups.manage");

  const [cats, setCats] = useState([]);
  const [current, setCurrent] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newRow, setNewRow] = useState({ code: "", label: "", order_index: 0 });
  const [creatingCategory, setCreatingCategory] = useState("");

  const loadCats = useCallback(async () => {
    try {
      const { data } = await api.get("/lookups/categories");
      setCats(data.items);
      if (!current && data.items.length) setCurrent(data.items[0]);
    } catch (e) {
      toast.error(classifyErr(e));
    }
  }, [current]);

  const load = useCallback(async () => {
    if (!current) return;
    setBusy(true);
    try {
      const { data } = await api.get("/lookups", { params: { category: current, include_inactive: true } });
      setItems(data.items);
    } catch (e) {
      toast.error(classifyErr(e));
    } finally {
      setBusy(false);
    }
  }, [current]);

  useEffect(() => {
    loadCats();
  }, [loadCats]);
  useEffect(() => {
    load();
  }, [load]);

  const saveItem = async (row, updates) => {
    try {
      await api.patch(`/lookups/${row.id}`, updates);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(classifyErr(e));
    }
  };

  const deactivate = async (row) => {
    try {
      await api.delete(`/lookups/${row.id}`);
      toast.success("Deactivated");
      load();
    } catch (e) {
      toast.error(classifyErr(e));
    }
  };

  const create = async () => {
    if (!newRow.code || !newRow.label) return;
    try {
      await api.post("/lookups", { category: current, ...newRow });
      toast.success("Added");
      setNewRow({ code: "", label: "", order_index: 0 });
      load();
    } catch (e) {
      toast.error(classifyErr(e));
    }
  };

  const createNewCategory = async () => {
    const cat = creatingCategory.trim().toLowerCase().replace(/\s+/g, "_");
    if (!cat) return;
    try {
      await api.post("/lookups", { category: cat, code: "first", label: "First value", order_index: 0 });
      toast.success("Category created");
      setCreatingCategory("");
      await loadCats();
      setCurrent(cat);
    } catch (e) {
      toast.error(classifyErr(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-lookups">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Lookups" }]}
        title="Lookups"
        subtitle="These values power dropdowns across the whole app. Deactivating hides a value but doesn't delete history."
        favorite={{ key: "admin.lookups", type: "screen", label: "Lookups", route: "/admin/lookups" }}
        testid="lookups-page-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <Card className="border-neutral-200 rounded-2xl lg:col-span-1">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2 px-2">
              <div className="text-xs font-semibold text-neutral-500 uppercase tracking-widest">Categories</div>
              <Button size="sm" variant="ghost" onClick={loadCats} className="h-7 w-7 p-0">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="space-y-0.5 max-h-[560px] overflow-y-auto">
              {cats.map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrent(c)}
                  data-testid={`lookup-cat-${c}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition ${
                    current === c ? "tulsi-gradient text-white shadow-sm" : "text-neutral-600 hover:bg-neutral-100"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            {canManage && (
              <div className="mt-3 border-t border-neutral-100 pt-3 px-2 space-y-2">
                <Label className="text-[11px]">New category</Label>
                <Input
                  data-testid="lookup-new-cat-input"
                  value={creatingCategory}
                  onChange={(e) => setCreatingCategory(e.target.value)}
                  placeholder="e.g. shelf_type"
                  className="h-9"
                />
                <Button size="sm" variant="outline" onClick={createNewCategory} className="w-full" data-testid="lookup-new-cat-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add category
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-neutral-200 rounded-2xl lg:col-span-3">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-400">Category</div>
                <div className="font-bold text-neutral-800">{current || "—"}</div>
              </div>
              <div className="text-xs text-neutral-500">{items.length} value(s)</div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-neutral-200">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium text-neutral-600">Code</th>
                    <th className="px-3 py-2 font-medium text-neutral-600">Label</th>
                    <th className="px-3 py-2 font-medium text-neutral-600 w-24">Order</th>
                    <th className="px-3 py-2 font-medium text-neutral-600 w-24">Active</th>
                    <th className="px-3 py-2 w-24" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {busy && (
                    <tr>
                      <td colSpan={5} className="text-center text-neutral-500 py-6">Loading…</td>
                    </tr>
                  )}
                  {!busy && items.map((it) => (
                    <EditableRow
                      key={it.id}
                      row={it}
                      canManage={canManage}
                      onSave={saveItem}
                      onDelete={deactivate}
                    />
                  ))}
                  {canManage && current && (
                    <tr className="bg-neutral-50/60" data-testid="lookup-new-row">
                      <td className="px-3 py-2">
                        <Input
                          data-testid="lookup-new-code"
                          value={newRow.code}
                          onChange={(e) => setNewRow({ ...newRow, code: e.target.value.trim() })}
                          placeholder="code"
                          className="h-9"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          data-testid="lookup-new-label"
                          value={newRow.label}
                          onChange={(e) => setNewRow({ ...newRow, label: e.target.value })}
                          placeholder="Label"
                          className="h-9"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          value={newRow.order_index}
                          onChange={(e) => setNewRow({ ...newRow, order_index: Number(e.target.value) })}
                          className="h-9 w-20"
                        />
                      </td>
                      <td className="px-3 py-2 text-neutral-400">—</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          onClick={create}
                          disabled={!newRow.code || !newRow.label}
                          className="tulsi-gradient text-white h-8"
                          data-testid="lookup-new-add"
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add
                        </Button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EditableRow({ row, canManage, onSave, onDelete }) {
  const [label, setLabel] = useState(row.label);
  const [order, setOrder] = useState(row.order_index);
  const [active, setActive] = useState(row.is_active);

  useEffect(() => {
    setLabel(row.label);
    setOrder(row.order_index);
    setActive(row.is_active);
  }, [row]);

  const dirty = label !== row.label || order !== row.order_index || active !== row.is_active;

  return (
    <tr data-testid={`lookup-row-${row.id}`}>
      <td className="px-3 py-2 font-mono text-xs text-neutral-600">{row.code}</td>
      <td className="px-3 py-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} disabled={!canManage} className="h-9" />
      </td>
      <td className="px-3 py-2">
        <Input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} disabled={!canManage} className="h-9 w-20" />
      </td>
      <td className="px-3 py-2">
        <Switch checked={active} onCheckedChange={setActive} disabled={!canManage} />
      </td>
      <td className="px-3 py-2 text-right space-x-1">
        {canManage && (
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={!dirty}
              onClick={() => onSave(row, { label, order_index: order, is_active: active })}
              className="h-8"
              data-testid={`lookup-save-${row.id}`}
            >
              <Save className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(row)}
              className="h-8 text-red-500 hover:text-red-600"
              data-testid={`lookup-del-${row.id}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </>
        )}
      </td>
    </tr>
  );
}
